import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AgencyClientService, AgencyService } from '../../../_services';
import { InvitationModel } from '../../../cms/pages/default/agency/profile/models/invitation.model';

/**
 * JS-1920 follow-up (2026-11-02): client user management surface, extracted from the
 * SettingsComponentComponent (/agency-client/profile/:id) so it can be mounted on the
 * client profile (/client/profile/:id) Settings tab instead. Behaviour is byte-for-byte
 * the same as the old settings-component block — the host just moves one page over.
 *
 * The old 'Invite Client Users' permission that used to gate the section is being
 * dropped from the DB; now that the whole surface lives on an agency-only route, the
 * route guard IS the gate — no per-call perm check needed in the component.
 */
@Component({
  selector: 'app-client-user-management',
  templateUrl: './client-user-management.component.html',
  styleUrls: ['./client-user-management.component.scss'],
  // Self-contained so the host doesn't have to wire its own ConfirmationService provider.
  providers: [ConfirmationService, MessageService],
})
export class ClientUserManagementComponent implements OnInit, OnChanges {
  // The client whose invitations this surface manages. On a fresh client-profile load
  // the id arrives in a later ngOnChanges cycle, so ngOnChanges handles the first fetch.
  @Input() clientId: number;

  public invitations: any[] = [];
  public invitationsColumns = [
    { name: 'role', data: 'role', sort: true, type: 'ClientRoleSelection' },
    { name: 'email', data: 'email', sort: true },
    {
      name: 'Date & Time of Invitation',
      data: 'created_at',
      sort: true,
      type: 'dateAndTime',
    },
    { name: 'status', data: 'status', sort: true, type: 'adminAndPMStatus' },
  ];
  public loading = true;

  public addClientModal = false;
  public isEditingInvitation = false;
  public invitation = new InvitationModel();

  public currentUser: any = null;
  public agencyId = 0;

  public readonly roles = [
    { label: 'Client Admin', value: 'client admin' },
    { label: 'Client Buyer', value: 'client buyer' },
    { label: 'Client Approver', value: 'client validator' },
    { label: 'Client Work Validator', value: 'client work validator' },
  ];

  constructor(
    private agencyService: AgencyService,
    private agencyClientService: AgencyClientService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    this.agencyId = this.currentUser && this.currentUser.agency_id ? this.currentUser.agency_id : 0;
    this.invitation.agency_id = this.agencyId;
    // Pin the profile's client id on the model as a default so every open-and-send cycle
    // writes it on the request. Backend falls back to the current user's own client_id
    // when the payload omits this, which breaks for agency users (whose own client_id
    // is null) — the invitation would land without a client association.
    this.invitation.client_id = this.clientId;
    if (this.clientId) {
      this.loadInvitations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clientId && this.clientId && !changes.clientId.previousValue) {
      this.loadInvitations();
    }
  }

  // Mirrors the pattern settings-component used — the agency-client getProfile() payload
  // carries the invitations list for the client. One request keeps us in step with every
  // existing call site that already relies on this shape.
  private loadInvitations(): void {
    this.loading = true;
    this.agencyClientService.getProfile(this.clientId).subscribe(
      (response: any) => {
        this.invitations = (response && response.invitations) || [];
        this.loading = false;
      },
      () => {
        this.loading = false;
      },
    );
  }

  openInviteModal(): void {
    this.isEditingInvitation = false;
    this.invitation = new InvitationModel();
    this.invitation.agency_id = this.agencyId;
    this.invitation.client_id = this.clientId;
    this.addClientModal = true;
  }

  openEditRoleModal(row: any): void {
    this.isEditingInvitation = true;
    this.invitation = new InvitationModel();
    this.invitation.agency_id = this.agencyId;
    this.invitation.email = row.email || '';
    this.invitation.role = row.role || '';
    this.invitation.language = row.language || '';
    this.invitation.message = row.message || '';
    // The surface is scoped to one client; keep that client on save.
    this.invitation.client_id = row.client_id || this.clientId;
    this.addClientModal = true;
  }

  closeInvitationModal(): void {
    this.addClientModal = false;
    this.isEditingInvitation = false;
    this.invitation = new InvitationModel();
    this.invitation.agency_id = this.agencyId;
    this.invitation.client_id = this.clientId;
  }

  saveInvitationModal(): void {
    if (this.isEditingInvitation) {
      this.saveInvitationRoleEdit();
    } else {
      this.makeInvitation();
    }
  }

  private saveInvitationRoleEdit(): void {
    const clientId = this.invitation.role && this.invitation.role.includes('client')
      ? (this.invitation.client_id || this.clientId)
      : null;

    if (this.invitation.role && this.invitation.role.includes('client') && !clientId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error!'),
        detail: this.translateService.instant('Please select a client!'),
      });
      return;
    }

    this.agencyService
      .updateUserRole({
        email: this.invitation.email,
        role: this.invitation.role,
        client_id: clientId,
      })
      .subscribe((response: any) => {
        this.translateService.get(response.message).subscribe((res) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: res,
          });
        });
        this.agencyClientService.getProfile(this.clientId).subscribe((refreshed: any) => {
          this.invitations = (refreshed && refreshed.invitations) || [];
        });
        this.closeInvitationModal();
      });
  }

  makeInvitation(): void {
    if (
      !this.invitation.email ||
      !this.invitation.role ||
      !this.invitation.language ||
      !this.invitation.message ||
      this.invitation.email.length === 0 ||
      this.invitation.role.length === 0 ||
      this.invitation.language.length === 0 ||
      this.invitation.message.length === 0
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error!'),
        detail: this.translateService.instant('Please give a valid email and select a role!'),
      });
      return;
    }
    this.agencyService.inviteClientMembers(this.invitation).subscribe((response: any) => {
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('Success!'),
        detail: this.translateService.instant(response.message),
      });
      this.invitations = response.invitations || this.invitations;
    });
    this.addClientModal = false;
    this.invitation = new InvitationModel();
    this.invitation.agency_id = this.agencyId;
    this.invitation.client_id = this.clientId;
  }

  confirmInvitationDelete(id: number): void {
    this.translateService.get('Are you sure you want to delete this?').subscribe((res) => {
      this.confirmationService.confirm({
        header: this.translateService.instant('Confirmation'),
        message: res,
        accept: () => this.deleteInvitation(id),
      });
    });
  }

  private deleteInvitation(id: number): void {
    this.agencyService.deleteInvitation({ id, agency_id: this.agencyId }).subscribe((response: any) => {
      if (response && response.status === 'success') {
        this.invitations = response.invitations || this.invitations;
      }
    });
  }

  updateRole(invitation: any): void {
    this.agencyService
      .updateUserRole({ email: invitation.email, role: invitation.role })
      .subscribe((response: any) => {
        this.translateService.get(response.message).subscribe((res) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: res,
          });
        });
      });
  }

  resendInvitation(email: string): void {
    this.agencyService.resendInvitation({ email }).subscribe((response: any) => {
      if (response && response.status === 'success') {
        this.translateService.get(response.message).subscribe((res) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: res,
          });
        });
      }
    });
  }

  reInviteUser(id: number): void {
    this.agencyService.reinviteUser({ id }).subscribe((response: any) => {
      if (response && response.status === 'success') {
        this.invitations = response.invitations || this.invitations;
      }
    });
  }
}
